import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { adminLogout, getAdminSession, getSettings, getReservations } from '../utils/api';
import useSessionKeepAlive from '../hooks/useSessionKeepAlive';
import { playNotificationSound } from '../utils/notifications';
import NotificationToast from '../components/NotificationToast';
import './AdminLayout.css';

const AdminLayout = () => {
  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userSettings, setUserSettings] = useState({});
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const lastReservationIdsRef = useRef(new Set());
  const pollingIntervalRef = useRef(null);
  const publicBaseUrl = process.env.REACT_APP_PUBLIC_SITE_URL || '/';

  useSessionKeepAlive(true);

  useEffect(() => {
    let isMounted = true;

    const clearAdminSession = () => {
      localStorage.removeItem('csrf_token');
      localStorage.removeItem('admin_user');
      window.dispatchEvent(new Event('admin-session-cleared'));
    };

    const redirectToLogin = () => {
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    };

    const validateSession = async () => {
      try {
        const response = await getAdminSession();
        const data = response.data.data || response.data;

        if (!isMounted) return;

        if (!data?.logged_in) {
          clearAdminSession();
          redirectToLogin();
          return;
        }

        if (data.user) {
          localStorage.setItem('admin_user', JSON.stringify(data.user));
          window.dispatchEvent(new Event('admin-session-updated'));
        }
      } catch (err) {
        if (!isMounted) return;

        if (err.response?.status === 401 || err.response?.status === 403) {
          clearAdminSession();
          redirectToLogin();
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateSession();
      }
    };

    validateSession();
    window.addEventListener('focus', validateSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      window.removeEventListener('focus', validateSession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await getSettings();
        const data = response.data.data || response.data;
        const settings = data.settings || {};
        setUserSettings(settings);
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    const checkNewReservations = async () => {
      if (!userSettings.desktop_notifications_enabled) return;

      try {
        const response = await getReservations();
        const allReservations = response.data.data || [];
        const currentIds = new Set(allReservations.map(r => r.id));

        if (lastReservationIdsRef.current.size > 0) {
          const newReservations = allReservations.filter(r => !lastReservationIdsRef.current.has(r.id));
          
          if (newReservations.length > 0) {
            const reservation = newReservations[0];
            
            playNotificationSound(userSettings.notification_sound || 'default');
            
            setNotificationData(reservation);
            setShowNotification(true);
            
            setTimeout(() => {
              setShowNotification(false);
            }, 5000);
          }
        }
        
        lastReservationIdsRef.current = currentIds;
      } catch (err) {
        console.error('Failed to check reservations:', err);
      }
    };

    if (userSettings.desktop_notifications_enabled) {
      checkNewReservations();
      pollingIntervalRef.current = setInterval(checkNewReservations, 30000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [userSettings]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      const publicBaseUrl = process.env.REACT_APP_PUBLIC_SITE_URL || '/';
      window.location.href = publicBaseUrl;
    } catch (error) {
      console.error('Logout failed:', error);
      localStorage.removeItem('admin_user');
      localStorage.removeItem('csrf_token');
      const publicBaseUrl = process.env.REACT_APP_PUBLIC_SITE_URL || '/';
      window.location.href = publicBaseUrl;
    }
  };

  return (
    <div className="admin-layout">
      {/* Admin Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2 className='text-center'>Administrácia</h2>
          <p className="text-muted text-sm mb-0 text-center"> Prihlásený:&nbsp;<b>
            {adminUser.cele_meno || adminUser.prihlasovacie_meno || 'Admin'} </b>
          </p>
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/admin/dashboard" className="sidebar-link" onClick={closeSidebar}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-1 0V4.5A.5.5 0 0 1 8 4zM3.732 5.732a.5.5 0 0 1 .707 0l.915.914a.5.5 0 1 1-.708.708l-.914-.915a.5.5 0 0 1 0-.707zM2 10a.5.5 0 0 1 .5-.5h1.586a.5.5 0 0 1 0 1H2.5A.5.5 0 0 1 2 10zm9.5 0a.5.5 0 0 1 .5-.5h1.5a.5.5 0 0 1 0 1H12a.5.5 0 0 1-.5-.5zm.754-4.246a.389.389 0 0 0-.527-.02L7.547 9.31a.91.91 0 1 0 1.302 1.258l3.434-4.297a.389.389 0 0 0-.029-.518z"/>
              <path fillRule="evenodd" d="M0 10a8 8 0 1 1 16 0 8 8 0 0 1-16 0zm8-7a7 7 0 0 0-2.219 13.657 8.978 8.978 0 0 1-.355-2.657A7.003 7.003 0 0 0 8 3z"/>
            </svg>
            <span>Váš prehľad</span>
          </NavLink>
          <NavLink to="/admin/reservations" className="sidebar-link" onClick={closeSidebar}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
            </svg>
            <span>Správa rezervácií</span>
          </NavLink>
          <NavLink to="/admin/services" className="sidebar-link" onClick={closeSidebar}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5V2zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1H4z"/>
            </svg>
            <span>Ponúkané služby</span>
          </NavLink>
          <NavLink to="/admin/blocked-times" className="sidebar-link" onClick={closeSidebar}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
              <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5z"/>
            </svg>
            <span>Blokované časy</span>
          </NavLink>
          <NavLink to="/admin/users" className="sidebar-link" onClick={closeSidebar}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/>
            </svg>
            <span>Administrátori</span>
          </NavLink>
          <NavLink to="/admin/gallery" className="sidebar-link" onClick={closeSidebar}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="16" rx="3" />
              <path d="M3 15l4-4 4 4 4-5 6 6" />
              <circle cx="9" cy="9" r="1.8" />
            </svg>
            <span>Galéria</span>
          </NavLink>
          <NavLink to="/admin/settings" className="sidebar-link" onClick={closeSidebar}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
              <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319z"/>
            </svg>
            <span>Nastavenia</span>
          </NavLink>
        </nav>

        <div className="sidebar-footer">
          <a href={publicBaseUrl} className="btn btn-primary btn-block" onClick={closeSidebar}>
            Späť na web
          </a>
          <button onClick={handleLogout} className="btn btn-secondary btn-block">
            Odhlásiť sa
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}

      {/* Admin Main Content */}
      <div className="admin-main">
        {/* Mobile Header */}
        <header className="admin-header">
          <div className="admin-header-content">
            {/* Hamburger Button */}
            <button 
              className={`hamburger ${sidebarOpen ? 'open' : ''}`}
              onClick={toggleSidebar}
              aria-label="Menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            
            <h1>Administrácia</h1>
          </div>
        </header>
        
        <main className="admin-content">
          <Outlet />
        </main>
      </div>

      {/* New Reservation Notification Toast */}
      <NotificationToast
        show={showNotification && notificationData}
        title={notificationData ? `Nová rezervácia - ${notificationData.nazov_sluzby}` : ''}
        message={notificationData?.meno}
        onClose={() => setShowNotification(false)}
      />
      {/* TODO: toto nejak krajšie urobiť, do viac riadkov a podobne... skontrolovať, či sa upozornenie vyskytuje v každej podstránke admin interfaceu */}
    </div>
  );
};

export default AdminLayout;
